import { useEffect, useMemo, useState } from 'react';
import { useWallet } from '../../context/WalletContext';
import {
  calcTimedOrderPreview,
  parseQuickOrderConfig,
  validateOrderAmount,
} from '../../utils/orderValidation';
import {
  formatPrice,
  listDurationOptions,
} from '../../utils/timedPlayConfig';

export default function TimedOrderModal({
  open,
  direction,
  products,
  primaryProduct,
  currentPrice,
  selectedProductId,
  amount,
  submitting,
  onClose,
  onSelectProduct,
  onAmountChange,
  onConfirm,
}) {
  const { balance, refreshWallet } = useWallet();
  const [localError, setLocalError] = useState('');

  const durationOptions = useMemo(
    () => listDurationOptions(products, primaryProduct?.marketSymbolCode),
    [products, primaryProduct?.marketSymbolCode]
  );

  const selectedOption = durationOptions.find(
    (item) => String(item.productId) === String(selectedProductId)
  );
  const selectedProduct = selectedOption?.product || primaryProduct;

  const quickOrder = useMemo(() => {
    const candidates = [selectedProduct, primaryProduct, ...(products || [])];
    for (const item of candidates) {
      const parsed = parseQuickOrderConfig(item?.playConfig);
      if (parsed.amountPresets.length > 0 || parsed.amountRatios.length > 0) {
        return parsed;
      }
    }
    return parseQuickOrderConfig(null);
  }, [selectedProduct, primaryProduct, products]);

  const preview = calcTimedOrderPreview(
    amount,
    selectedProduct?.handlingFeeRate,
    selectedOption?.payoutOdds
  );

  const validation = useMemo(
    () => validateOrderAmount(amount, selectedProduct, balance, selectedProduct?.handlingFeeRate),
    [amount, selectedProduct, balance]
  );

  const canConfirm =
    Boolean(selectedProductId) &&
    validation.ok &&
    !submitting;

  useEffect(() => {
    if (!open) {
      return;
    }
    setLocalError('');
    refreshWallet();
  }, [open, refreshWallet]);

  if (!open) {
    return null;
  }

  const isUp = direction === 'UP';
  const directionLabel = isUp ? '買漲' : '買跌';
  const balanceNum = Number(balance);
  const usableBalance = Number.isNaN(balanceNum) ? 0 : balanceNum;

  function applyPreset(value) {
    setLocalError('');
    onAmountChange(String(value));
  }

  function applyRatio(ratio) {
    if (usableBalance <= 0) {
      setLocalError('USDT 餘額不足');
      return;
    }
    setLocalError('');
    onAmountChange((usableBalance * ratio).toFixed(2));
  }

  function handleConfirmClick() {
    if (!validation.ok) {
      setLocalError(validation.message);
      return;
    }
    setLocalError('');
    onConfirm({ productId: Number(selectedProductId) });
  }

  return (
    <div className="trade-modal">
      <button type="button" className="trade-modal__backdrop" aria-label="關閉" onClick={onClose} />
      <div className="trade-modal__panel">
        <header className="trade-modal__header">
          <h2>限時交易</h2>
          <button type="button" className="trade-modal__close" aria-label="關閉" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="trade-modal__summary">
          <div className="trade-modal__row">
            <span>方向</span>
            <strong className={isUp ? 'is-up' : 'is-down'}>{directionLabel}</strong>
          </div>
          <div className="trade-modal__row">
            <span>產品名稱</span>
            <strong>{primaryProduct?.displayName || primaryProduct?.marketSymbolCode}</strong>
          </div>
          <div className="trade-modal__row">
            <span>現價</span>
            <strong>{formatPrice(currentPrice)}</strong>
          </div>
        </div>

        <div className="trade-modal__section">
          <div className="trade-modal__label">結算時間</div>
          <div className="trade-modal__duration-grid">
            {durationOptions.map((option) => {
              const active = String(option.productId) === String(selectedProductId);
              return (
                <button
                  key={option.productId}
                  type="button"
                  className={`trade-modal__duration ${active ? 'is-active' : ''}`}
                  onClick={() => onSelectProduct(option.productId)}
                >
                  <span>{option.durationSeconds}秒</span>
                  <small>收益{option.payoutPercent || Math.round(option.payoutOdds * 100)}%</small>
                </button>
              );
            })}
          </div>
        </div>

        <div className="trade-modal__section">
          <div className="trade-modal__label">下單金額</div>
          <input
            className="trade-modal__input"
            type="number"
            min="0"
            step="0.01"
            placeholder="請輸入金額"
            value={amount}
            onChange={(e) => {
              setLocalError('');
              onAmountChange(e.target.value);
            }}
          />
          <div className="trade-modal__preset-grid">
            {quickOrder.amountPresets.map((preset) => (
              <button
                key={preset}
                type="button"
                className="trade-modal__preset-btn"
                onClick={() => applyPreset(preset)}
              >
                {preset}
              </button>
            ))}
          </div>
          <div className="trade-modal__preset-grid">
            {quickOrder.amountRatios.map((ratio) => (
              <button
                key={ratio}
                type="button"
                className="trade-modal__preset-btn"
                onClick={() => applyRatio(ratio)}
              >
                {Math.round(ratio * 100)}%
              </button>
            ))}
          </div>
        </div>

        <div className="trade-modal__metrics">
          <div><span>USDT 餘額</span><strong>{balance ?? '—'}</strong></div>
          <div><span>佔用保證金</span><strong>{formatPrice(preview.margin, 2)}</strong></div>
          <div><span>手續費</span><strong>{formatPrice(preview.fee, 2)}</strong></div>
          <div><span>預期收益</span><strong>{formatPrice(preview.expectedProfit, 2)}</strong></div>
        </div>

        {(localError || (!validation.ok && amount)) && (
          <div className="trade-modal__error">{localError || validation.message}</div>
        )}

        <button
          type="button"
          className={`trade-modal__confirm ${canConfirm ? 'is-ready' : ''}`}
          disabled={!canConfirm}
          onClick={handleConfirmClick}
        >
          {submitting ? '下單中…' : `${directionLabel} 確認下單`}
        </button>
      </div>
    </div>
  );
}
